/**
 * useVendorStatus - Determines if the current user is a vendor and fetches their entities
 * Calls GET /vendor/status as the authoritative source so the tab appears correctly
 * immediately after login, regardless of what the login response contains.
 * @atomic @idempotent
 */
import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../context/AuthContext'
import { useAsyncOperation } from './useAsyncOperation'
import { vendorAPI } from '../services/api'

export function useVendorStatus() {
  const { isAuthenticated } = useAuth()
  const { loading, error, execute } = useAsyncOperation()
  const [isVendor, setIsVendor] = useState(false)
  const [vendorEntities, setVendorEntities] = useState([])

  const fetchStatus = useCallback(async () => {
    if (!isAuthenticated) {
      setIsVendor(false)
      setVendorEntities([])
      return
    }
    await execute(async () => {
      const statusRes = await vendorAPI.getStatus()
      const vendorStatus = statusRes.data?.isVendor ?? false
      setIsVendor(vendorStatus)

      if (vendorStatus) {
        const entitiesRes = await vendorAPI.getEntities()
        setVendorEntities(entitiesRes.data?.entities ?? [])
      } else {
        setVendorEntities([])
      }
    }, { silent: true })
  }, [isAuthenticated, execute])

  useEffect(() => {
    fetchStatus()
  }, [fetchStatus])

  return {
    isVendor,
    vendorEntities,
    loading,
    error,
    refetch: fetchStatus
  }
}
